/**
 * 형제 그룹 칩 — 같은 점검(들)에서 같은 템플릿으로 만들어진 문서 사이를 오가는 **내비게이션**.
 *
 * 결정: `docs/decisions/2026-09-01-composer-split-generation.md` §④.
 *
 * **탭이 아니다.** 편집 권한·저장은 여전히 문서별로 독립이고 이 컴포넌트는 이동만 한다 —
 * 상태를 소유하지 않으므로 형제로 옮겨도 "다른 탭을 어떻게 할지"라는 문제가 애초에 없다
 * (탭 안을 기각한 이유가 그것이다).
 *
 * ⚠️ **같은 칩이지만 호스트마다 동작이 갈린다** — 목적지(`resolveHref`)·이동 가로채기
 * (`interceptNavigate`)·진입 축(`navState`) 셋 다 호스트가 준다. 상세에서 옆 칩을 누르는 것은
 * 「옆 문서를 읽겠다」이고, 작성 화면에서 누르는 것은 「하다 만 데로 돌아가겠다」라 **다른
 * 행위**이기 때문이다: 상세는 진입 축을 실어 그 문서가 배포 설정으로 끌려가지 않게 하고,
 * 작성 화면은 싣지 않아 완료된 건이 종전대로 하다 만 배포 설정으로 간다(S6 —
 * `docs/decisions/2026-09-06-tbm-post-submit-distribution-and-delegation.md` §5).
 * 그러니 이 파일에 「상세와 작성 화면은 같게 동작한다」는 전제를 두지 말 것.
 *
 * **도메인 지식이 0인 것이 요점이다.** 그룹의 정체는 `reportInspections` junction +
 * `templateKey`로만 정의된다 — 공종인지 장소인지 이 컴포넌트는 모른다. 그래서 나중에 다른
 * 템플릿이 분할을 쓰면 아무것도 안 해도 같은 UI를 받는다. 같은 이유로 **노출도 파생**이다:
 * policy 플래그로 켜는 게 아니라 형제가 2건 이상이면 뜬다.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@safetyhub/ui';
import { DELEGATION_PROGRESS_LABELS } from '@safetyhub/shared';
import { getDocStatusBadgeTone, type Report } from '../../lib/reports';
import { resolveReportStatusLabel } from '../../lib/reportStatusLabel';
import { disambiguateSiblingLabels } from '../../lib/siblingLabels';
import { useSiblingReports } from './useSiblingReports';
import type { DetailEntryState } from './manifest/detailBackNav';
import type { SiblingNavState } from './siblingNavState';

export interface SiblingReportChipsProps {
  /** 지금 보고 있는 문서. 형제 조회의 기준(점검·템플릿)이자 "현재 칩" 판정의 축. */
  report: Report;
  /** 목록 조회 스코프. 없으면(멤버십 로딩 중) 조회하지 않는다. */
  companyId?: string;
  /**
   * 이 상세로 들어온 진입점(`location.state`). 형제로 이동해도 **그대로 전파**해
   * 뒤로가기가 원래 진입점(결재함·점검 상세 등)을 유지하게 한다.
   */
  entryState?: DetailEntryState | null;
  /**
   * 이동에 함께 실을 **진입 축**(`siblingNavState`). 호스트가 정한다 — 칩은 옮기기만 한다.
   *
   * 상세는 `{ viaSiblingChip: true }`를 준다: 형제 칩 이동은 「옆 문서를 보겠다」이지 새
   * 진입이 아니므로, 그 문서의 진입 라우팅이 배포 설정으로 끌고 가면 안 된다.
   * **작성 화면은 주지 않는다** — S6 계약이 「작성 화면의 형제 칩에서 완료(초안) 건을 누르면
   * 하다 만 배포 설정으로」이기 때문이다(결정 2026-09-06 §5). 목적지(`resolveHref`)와 같은
   * 이유로 호스트 소유다: 무엇이 「새 진입」인지는 호스트가 안다(§④ "도메인 지식 0").
   */
  navState?: SiblingNavState;
  className?: string;
  /**
   * 형제 칩의 이동 목적지. 미지정이면 문서 상세(`/reports/:id`).
   *
   * 작성 화면(new-composed 편집 모드)이 이 컴포넌트를 재사용할 때 필요하다 — 거기서는
   * 편집 가능한 형제를 **그 형제의 편집 URL**로 열어야 순회가 이어진다. 목적지 판정을
   * 여기 두지 않는 이유는 §④의 "도메인 지식 0" 원칙이다: 무엇이 편집 가능한지는 호스트가
   * 안다(작성 화면은 자기 편집 축을, 상세는 "언제나 상세"를).
   */
  resolveHref?: (sibling: Report) => string;
  /**
   * 이동 직전 가로채기. `true`를 반환하면 링크의 기본 이동을 막는다 — 호스트가 확인 후
   * 직접 이동한다(작성 화면의 미저장 이탈 가드). 미지정이면 그대로 이동.
   */
  interceptNavigate?: (href: string, sibling: Report) => boolean;
}

export function SiblingReportChips({
  report,
  companyId,
  entryState,
  navState,
  className,
  resolveHref,
  interceptNavigate,
}: SiblingReportChipsProps) {
  // 조회·정렬은 공용 훅이 소유한다 — 작성 화면이 같은 목록으로 잔여 초안·다음 형제를 세므로
  // 여기서 키를 손수 적으면 두 화면이 서로 다른 답을 볼 수 있다(useSiblingReports doc).
  const { siblings, error } = useSiblingReports(report, companyId);

  // 내비게이션 보조라 실패는 **조용히 미노출**로 접는다(에러 배너를 띄우면 본문 읽기를
  // 방해한다). 다만 삼키지는 않는다 — 원인 없는 "칩이 안 뜬다"를 만들지 않기 위해.
  useEffect(() => {
    if (error) console.warn('[SiblingReportChips] 형제 문서 조회 실패', error);
  }, [error]);

  // 형제가 자기 자신뿐이면 오갈 곳이 없다 — 칩 줄을 통째로 숨긴다(노출은 파생, §④).
  if (siblings.length < 2) return null;

  // 훅이 생성 순으로 정렬해 돌려준다(칩 순서 = "다음 형제" 순서).
  const ordered = siblings;

  /**
   * 라벨은 그룹 표시명 → 없으면 제목. 키(`splitGroupKey`)는 가드의 축이라 사람이 읽는 값이
   * 아닐 수 있어 절대 라벨로 쓰지 않는다.
   *
   * **중복 해소는 공용 규칙을 쓴다**(2026-09-10 행 축 전환의 귀결) — 같은 공종의 남은 행으로
   * 문서를 또 만들 수 있게 되면서 `splitGroupLabel`이 같은 형제가 둘 생길 수 있고, 그러면 이
   * 줄에서 두 칩의 글자가 완전히 같아진다. 무인증 담당자 화면(`SplitSiblingBar`)과 **같은
   * 함수**를 써야 두 화면이 같은 형제를 같은 이름으로 부른다.
   */
  const labels = disambiguateSiblingLabels(
    ordered.map((sibling) => sibling.splitGroupLabel?.trim() || sibling.title),
  );

  return (
    <nav aria-label="형제 문서" className={className} data-testid="sibling-report-chips">
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
        {ordered.map((sibling, index) => {
          const label = labels[index];
          const isCurrent = sibling.id === report.id;
          /**
           * 상태 배지 — 작성 단계에서는 **위임 진행**이 더 정확하다(2026-09-10).
           *
           * `status`만 쓰면 확장 직후 형제가 전부 「생성 중」이라 진척이 안 보인다. 서버가
           * 내리는 `delegationProgress`는 이제 **문서별**이라(앵커 토큰의 저장 신호를 빌리지
           * 않는다) 어느 형제를 쓰고 있는지 이 줄에서 갈린다. 판정은 서버가 하고 여기서는
           * 라벨만 붙인다 — 화면에서 다시 계산하면 두 곳이 갈린다.
           * 진행이 없으면(위임 아님·해제됨·결재완료) 종전 상태 배지 그대로.
           */
          const progressLabel = sibling.delegationProgress
            ? DELEGATION_PROGRESS_LABELS[sibling.delegationProgress.state]
            : null;
          const status = progressLabel ? (
            <Badge tone="amber" size="sm" dot>
              {progressLabel}
            </Badge>
          ) : (
            <Badge tone={getDocStatusBadgeTone(sibling.status)} size="sm" dot>
              {resolveReportStatusLabel(sibling)}
            </Badge>
          );
          const chipBody = (
            <>
              <span className="max-w-[10rem] truncate">{label}</span>
              {status}
            </>
          );

          // 현재 문서는 링크가 아니다 — 자기 자신으로 가는 링크는 내비게이션이 아니라 소음이다.
          if (isCurrent) {
            return (
              <span
                key={sibling.id}
                aria-current="true"
                title={label}
                className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 px-3 py-1.5 text-sm font-semibold text-brand-700"
              >
                {chipBody}
              </span>
            );
          }

          const href = resolveHref ? resolveHref(sibling) : `/reports/${sibling.id}`;

          return (
            <Link
              key={sibling.id}
              to={href}
              // 진입점 전파 — 형제로 옮겨도 뒤로가기가 원래 들어온 곳을 가리킨다.
              // 진입 축(`navState`)은 호스트가 준 것을 그대로 얹는다(미지정이면 종전 그대로).
              state={{ from: entryState?.from, fromLabel: entryState?.fromLabel, ...navState }}
              // 가로채기는 `preventDefault`로만 한다 — href는 남겨 둬야 새 탭 열기·주소 복사가
              // 계속 동작한다(이동 자체를 onClick으로 옮기면 그게 죽는다).
              onClick={(e) => {
                if (interceptNavigate?.(href, sibling)) e.preventDefault();
              }}
              title={label}
              className="inline-flex items-center gap-1.5 rounded-full border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-600 hover:border-ink-300 hover:bg-ink-50"
            >
              {chipBody}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export default SiblingReportChips;
